import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import Presenter from './Presenter';
import { IContainerProps, ISignUpFormData } from './types';
import actions from '../../state/actions';
import selectors from '../../state/selectors';
import { IAppState } from '../../state/types';
import { validateEmail, validatePassword, validateName, validatePasswordMatch } from '../../utils/validation';

const PLATFORM_BASE_URL = `${process.env.REACT_APP_PLATFORM_API_BASE_URL}`;

const SignUpContainer = (props: IContainerProps) => {
    const [formData, setFormData] = useState<ISignUpFormData>({
        name: '',
        email: '',
        password: '',
        confirmPassword: ''
    });

    const [errors, setErrors] = useState<Partial<ISignUpFormData>>({});
    const [loading, setLoading] = useState(false);
    const [shouldNavigate, setShouldNavigate] = useState(false);
    const [emailExists, setEmailExists] = useState(false);
    const [checkingEmail, setCheckingEmail] = useState(false);
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation<{ from?: { pathname: string; state?: any } | string }>();
    const authUser = useSelector((state: IAppState) => selectors.auth.getAuthUser(state));
    const rehydrated = useSelector((state: IAppState) => selectors.auth.getRehydrated(state));

    // Extract destination path as stable primitive
    const fromPath = location.state?.from || '/';

    const onChange = (e: React.ChangeEvent<HTMLInputElement>, field: keyof ISignUpFormData) => {
        const value = e.target.value;
        setFormData(prev => ({ ...prev, [field]: value }));

        // Clear field error on change
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }

        // Reset email exists flag when email changes
        if (field === 'email') {
            setEmailExists(false);
        }
    };

    const checkEmailExists = async (email: string) => {
        if (!email || !email.includes('@')) return;

        const normalizedEmail = email.toLowerCase().trim();
        setCheckingEmail(true);

        try {
            const response = await axios.get(
                `${PLATFORM_BASE_URL}/user/check-email/${encodeURIComponent(normalizedEmail)}`
            );

            if (response.data?.hasAccount) {
                setEmailExists(true);
                setErrors(prev => ({
                    ...prev,
                    email: 'Ya existe una cuenta con este email'
                }));
            } else {
                setEmailExists(false);
            }
        } catch (error) {
            console.error('Error checking email:', error);
            // Don't block signup on check failure
        } finally {
            setCheckingEmail(false);
        }
    };

    const onEmailBlur = () => {
        checkEmailExists(formData.email);
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<ISignUpFormData> = {};
        
        const nameError = validateName(formData.name);
        if (nameError) newErrors.name = nameError;
        
        const emailError = validateEmail(formData.email);
        if (emailError) newErrors.email = emailError;
        
        const passwordError = validatePassword(formData.password);
        if (passwordError) newErrors.password = passwordError;
        
        const confirmPasswordError = validatePasswordMatch(formData.password, formData.confirmPassword);
        if (confirmPasswordError) newErrors.confirmPassword = confirmPasswordError;
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validateForm()) return;

        // Block submit if email already exists
        if (emailExists) {
            setErrors(prev => ({
                ...prev,
                email: 'Ya existe una cuenta con este email'
            }));
            return;
        }

        setLoading(true);
        dispatch(actions.auth.signUpRequest(
            formData.name,
            formData.email.toLowerCase(),
            formData.password,
            handleSignUpResult
        ));
    };

    const handleSignUpResult = (result: any, error: any) => {
        setLoading(false);
        if (error) {
            // Extract actual backend error message for better debugging
            const errorMessage = error?.response?.data?.message
                || error?.message
                || 'Error al crear la cuenta. Inténtalo de nuevo.';
            setErrors({ email: errorMessage });
        } else {
            // Explicitly trigger navigation on success
            setShouldNavigate(true);
        }
    };

    // Navigate ONLY when explicitly triggered by successful signup
    useEffect(() => {
        if (shouldNavigate && authUser && rehydrated) {
            history.replace(fromPath);
            // No need to reset flag - component will unmount after navigation
        }
    }, [shouldNavigate, authUser, rehydrated, fromPath, history]);

    return (
        <Presenter
            formData={formData}
            errors={errors}
            loading={loading}
            onChange={onChange}
            onSubmit={handleSubmit}
            onEmailBlur={onEmailBlur}
            emailExists={emailExists}
            checkingEmail={checkingEmail}
        />
    );
};

export default SignUpContainer;